import { GreenhouseV3ConnectErrorCode } from '@dadohr/shared/domainTypes/integrations/greenhouseV3';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { useDialogState } from '../../../../../../hooks/useDialogState';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../../i18next';
import { useStringQueryParam } from '../../../../../../routes';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectContainer } from '../../../../../OAuth/pages/Redirect';
import { IntegrationSetupCompleteModal } from '../common';
import { getGreenhouseV3AuthorizeUrl } from './service';

const NAME = 'Greenhouse';

// User-friendly copy per backend error code. The four typed codes come from the
// shared GreenhouseV3ConnectErrorCode enum; the backend's generic redirect
// reasons (no_state / no_code / redirect_error / unknown), plus any missing or
// unexpected value, fall through to the default retry copy. The authorizing
// Greenhouse user must be a Site Admin — repeated in the footer below so it
// shows for every failure, not only the NOT_SITE_ADMIN case.
const errorMessage = (error: string, t: UnsafeTFunction): string => {
  switch (error) {
    case GreenhouseV3ConnectErrorCode.NOT_SITE_ADMIN:
      return t(
        'The Greenhouse user who authorized the connection is not a Site Admin. Please have a Greenhouse Site Admin authorize Dado.'
      );
    case GreenhouseV3ConnectErrorCode.MISSING_SCOPES:
      return t(
        'Dado was not granted all the permissions it needs. Please re-authorize and accept the requested scopes.'
      );
    case GreenhouseV3ConnectErrorCode.OAUTH_STATE_INVALID:
      return t('Your session expired. Please try connecting again.');
    case GreenhouseV3ConnectErrorCode.INVALID_CREDENTIALS:
      return t('The authorization was invalid. Please try connecting again.');
    default:
      return t('The connection failed. Please try again.');
  }
};

export const PageGreenhouseV3OAuthRedirect = () => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();

  const [orgIdParam] = useStringQueryParam('orgId');
  const [error] = useStringQueryParam('error');

  const {
    dialogOpen: successDialogOpen,
    closeDialog: closeSuccessDialog
  } = useDialogState(true);

  // Success is signalled by the backend redirecting back with `?orgId=`. An
  // `error` param always wins so a malformed URL never reads as connected.
  const connected = !error && !!orgIdParam;

  if (connected) {
    return (
      <PageOAuthRedirectContainer>
        <Typography component="h3" variant="h3">
          {t('oauth.setupComplete', { name: NAME })}.
        </Typography>
        <br />
        <div>{t('oauth.setupCompleteNextSteps')}</div>
        <IntegrationSetupCompleteModal
          integrationName={NAME}
          open={successDialogOpen}
          onClose={closeSuccessDialog}
        />
      </PageOAuthRedirectContainer>
    );
  }

  return (
    <PageOAuthRedirectContainer>
      <Typography component="h3" variant="h3">
        {t('oauth.setupFailed', { name: NAME })}.
      </Typography>
      <br />
      <Alert severity="error">{errorMessage(error, t)}</Alert>
      <div>
        {t(
          'The Greenhouse user who authorizes the connection must be a Site Admin.'
        )}
      </div>
      <ButtonWithPromise
        variant="contained"
        color="primary"
        onClick={async () => {
          const url = await getGreenhouseV3AuthorizeUrl(org.id);
          window.location.href = url;
        }}
        pending={`${t('org.integrations.actions.redirecting')}...`}
      >
        {t('Retry')}
      </ButtonWithPromise>
    </PageOAuthRedirectContainer>
  );
};
